const MYU_LOGO_SRC = import.meta.env.VITE_MYU_LOGO_URL ?? '/branding/myu-logo.png';

export interface ProfileFlyerContent {
  readonly name: string;
  readonly school: string;
  readonly shareUrl: string;
  readonly qrDataUri?: string;
}

interface LoadedImage {
  readonly dataUrl: string;
  readonly width: number;
  readonly height: number;
}

function resolveAssetUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (typeof window === 'undefined') {
    return path;
  }
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
}

function flyerFilename(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug || 'profile'}-flyer.pdf`;
}

async function loadImageAsDataUrl(src: string): Promise<LoadedImage> {
  const response = await fetch(resolveAssetUrl(src));
  if (!response.ok) {
    throw new Error('Could not load MyU logo for the flyer.');
  }
  const blob = await response.blob();
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Could not read logo image.'));
    reader.readAsDataURL(blob);
  });

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        dataUrl,
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.onerror = () => reject(new Error('Could not decode logo image.'));
    image.src = dataUrl;
  });
}

async function rasterizeSvgDataUri(svgDataUri: string, sizePx: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = sizePx;
      canvas.height = sizePx;
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('Could not render QR code.'));
        return;
      }
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, sizePx, sizePx);
      context.drawImage(image, 0, 0, sizePx, sizePx);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => reject(new Error('Could not render QR code.'));
    image.src = svgDataUri;
  });
}

async function resolveQrPngDataUrl(shareUrl: string, qrDataUri?: string): Promise<string> {
  if (qrDataUri) {
    return rasterizeSvgDataUri(qrDataUri, 512);
  }
  const encoded = encodeURIComponent(shareUrl);
  return loadImageAsDataUrl(
    `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${encoded}`,
  ).then((item) => item.dataUrl);
}

export function svgElementToDataUri(svg: SVGSVGElement): string {
  const serialized = new XMLSerializer().serializeToString(svg);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;
}

/** Builds and downloads a profile flyer PDF with the MyU logo, QR code, and profile details. */
export async function downloadProfileFlyer(content: ProfileFlyerContent): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a5', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const horizontalMargin = 18;

  const logo = await loadImageAsDataUrl(MYU_LOGO_SRC);
  const logoMaxWidthMm = 52;
  const logoHeightMm = (logo.height / logo.width) * logoMaxWidthMm;
  const logoX = (pageWidth - logoMaxWidthMm) / 2;

  let cursorY = 20;
  doc.addImage(logo.dataUrl, 'PNG', logoX, cursorY, logoMaxWidthMm, logoHeightMm);

  cursorY += logoHeightMm + 14;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(17, 24, 39);
  doc.text(content.name, pageWidth / 2, cursorY, { align: 'center' });

  cursorY += 9;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(107, 114, 128);
  doc.text(content.school, pageWidth / 2, cursorY, { align: 'center' });

  cursorY += 14;
  const qrSizeMm = 72;
  const qrPng = await resolveQrPngDataUrl(content.shareUrl, content.qrDataUri);
  doc.addImage(qrPng, 'PNG', (pageWidth - qrSizeMm) / 2, cursorY, qrSizeMm, qrSizeMm);

  cursorY += qrSizeMm + 12;
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  const urlLines = doc.splitTextToSize(content.shareUrl, pageWidth - horizontalMargin * 2);
  doc.text(urlLines, pageWidth / 2, cursorY, { align: 'center' });

  doc.save(flyerFilename(content.name));
}
